<template>
  <Teleport v-if="voyadaEnabled" to="body">
    <div class="desk-fabs">
      <button
        id="voyada-vue-fab"
        :class="['desk-fab', isVoyadaOpen && 'desk-fab--active']"
        :aria-label="isVoyadaOpen ? 'Close Voyada AI assistant' : 'Open Voyada AI assistant'"
        @click="chatStore.toggleVoyada()"
      >
        <span v-if="isVoyadaOpen" class="zoho-label">✕</span>
        <img v-else :src="voyadaIconUrl" width="24" height="24" alt="Voyada" />
      </button>
      <button
        v-if="zohoReady"
        :class="['desk-fab', 'desk-fab--enter', isSupportOpen && 'desk-fab--active']"
        aria-label="Open Zoho support chat"
        @click="chatStore.toggleSupport()"
      >
        <span v-if="isSupportOpen" class="zoho-label">✕</span>
        <img v-else class="zoho-icon" :src="zohoIconUrl" width="60" height="60" alt="Zoho support chat" />
      </button>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useChatStore } from '@/stores/ChatStore'
import { useVoyadaFlag } from '@/composables/useVoyadaFlag'
import voyadaIconUrl from '@/assets/icon/voyada-nav.svg'
import zohoIconUrl from '@/pages/public/images/zoho-chat-icon.svg'

const { enabled: voyadaEnabled } = useVoyadaFlag()

const chatStore = useChatStore()
const { activeChat } = storeToRefs(chatStore)
const { onZohoClosed, onVoyadaClosed, onVoyadaOpened } = chatStore

const isVoyadaOpen = computed(() => activeChat.value === 'voyada')
const isSupportOpen = computed(() => activeChat.value === 'support')

// The Zoho FAB only appears once the SalesIQ script has finished loading, so a
// visible button always means a click opens the chat immediately — never a
// pending/spinner state. The init script auto-loads the script on page load
// (scheduleLoad), so we just wait for its ready signal here.
const zohoReady = ref(window.__ttgZohoSalesIq?.ready ?? false)

const sdkFabObservers: MutationObserver[] = []
let zohoObserver: MutationObserver | null = null
let voyadaObserver: MutationObserver | null = null
let zohoObserverPoll: number | null = null

function suppressSdkFab(fab: HTMLElement) {
  fab.style.setProperty('display', 'none', 'important')
  const observer = new MutationObserver(() => {
    fab.style.setProperty('display', 'none', 'important')
  })
  observer.observe(fab, { attributes: true, attributeFilter: ['style'] })
  sdkFabObservers.push(observer)
}

function suppressById(id: string) {
  const existing = document.getElementById(id)
  if (existing) {
    suppressSdkFab(existing)
  } else {
    const bodyObserver = new MutationObserver(() => {
      const el = document.getElementById(id)
      if (el) {
        suppressSdkFab(el)
        bodyObserver.disconnect()
      }
    })
    bodyObserver.observe(document.body, { childList: true, subtree: true })
  }
}

let injectedStyle: HTMLStyleElement | null = null

function watchVoyadaDrawer() {
  const drawer = document.querySelector('#voyada-drawer')
  if (!drawer) return false

  let wasOpen = drawer.classList.contains('is-open')
  voyadaObserver = new MutationObserver(() => {
    const isOpen = drawer.classList.contains('is-open')
    if (isOpen === wasOpen) return
    wasOpen = isOpen
    isOpen ? onVoyadaOpened() : onVoyadaClosed()
  })
  voyadaObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] })
  return true
}

function watchZohoWindow() {
  const zohoWindow = document.querySelector('#zsiq_chat_wrap') as HTMLElement | null
  if (!zohoWindow) return false

  zohoObserver = new MutationObserver(() => {
    if (zohoWindow.style.display === 'none') onZohoClosed()
  })
  zohoObserver.observe(zohoWindow, { attributes: true, attributeFilter: ['style'] })
  return true
}

onMounted(() => {
  const loader = window.__ttgZohoSalesIq
  loader?.setCustomLauncher(true)
  // onReady fires synchronously if already ready, otherwise once loading completes.
  loader?.onReady(() => {
    zohoReady.value = true
  })
  suppressById('voyada-fab')
  suppressById('zsiq_cus_sticker')
  watchVoyadaDrawer()
  let attempts = 0
  zohoObserverPoll = window.setInterval(() => {
    if (!zohoObserver) watchZohoWindow()
    if (zohoObserver || ++attempts > 20) {
      if (zohoObserverPoll !== null) window.clearInterval(zohoObserverPoll)
      zohoObserverPoll = null
    }
  }, 500)
  window.addEventListener('voyada-widget:open', onVoyadaOpened as EventListener)
  window.addEventListener('voyada-widget:close', onVoyadaClosed as EventListener)
  injectedStyle = document.createElement('style')
  injectedStyle.textContent = `
    #zsiq_float { transform: translate3d(-33px, -270px, 0px); }
    @media screen and (min-width: 752px) {
      #zsiq_chat_wrap,
      .zls-sptwndw,
      .chat-iframe-wrap {
        right: 85px !important;
        bottom: 2rem !important;
      }
    }
  `
  document.head.appendChild(injectedStyle)
})

onUnmounted(() => {
  sdkFabObservers.forEach((o) => o.disconnect())
  zohoObserver?.disconnect()
  voyadaObserver?.disconnect()
  if (zohoObserverPoll !== null) window.clearInterval(zohoObserverPoll)
  window.removeEventListener('voyada-widget:open', onVoyadaOpened as EventListener)
  window.removeEventListener('voyada-widget:close', onVoyadaClosed as EventListener)
  injectedStyle?.remove()
})
</script>

<style scoped>
.desk-fabs {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;
}

@media screen and (max-width: 751px) {
  .desk-fabs {
    display: none;
  }
}

.desk-fab {
  display: flex;
  width: 60px;
  height: 60px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 1000px;
  background: #fff;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.25);
  border: none;
  cursor: pointer;
  transition: box-shadow 0.15s;
  padding: 0;
  pointer-events: auto;
}

.desk-fab:hover {
  box-shadow: 0 0 14px 0 rgba(0, 0, 0, 0.35);
}

/* Fade the Zoho FAB in once SalesIQ is ready, so its delayed appearance reads
   as intentional rather than a layout pop. */
.desk-fab--enter {
  animation: desk-fab-fade-in 0.2s ease-out;
}

@keyframes desk-fab-fade-in {
  from {
    opacity: 0;
    transform: scale(0.85);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.desk-fab--active {
  box-shadow:
    0 0 0 2px #109d94,
    0 0 8px 0 rgba(0, 0, 0, 0.25);
}

.zoho-label {
  color: #000;
  font-family: Poppins;
  font-size: 8px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.zoho-icon {
  display: block;
  width: 60px;
  height: 60px;
  border-radius: 1000px;
  object-fit: contain;
}
</style>
